import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import cyberInsuranceReportAPI from "../services/CyberInsuranceReportAPI";
import { useUser } from "../context/UserContext";
import { useTranslation } from "../../../i18n/LanguageContext";
import LoadingSpinner from "../common/LoadingSpinner";
import { showToastWithAction } from "../utils/ToastWithAction";
import "../css/Common.css";
import "./css/CyberInsuranceReport.css";

/**
 * Standard administrative controls the account admin self-attests to.
 * Keys MUST match the backend buildAttestations() standard control keys.
 * Labels/help text are resolved via i18n at render time using the key.
 */
const STANDARD_ATTESTATIONS = [
  { key: "edr" },
  { key: "backups" },
  { key: "patching" },
  { key: "email_filtering" },
  { key: "incident_response" },
  { key: "access_control" },
];

const PERIOD_OPTIONS = [30, 60, 90, 180, 365];

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function statusClass(status) {
  switch (status) {
    case "STRONG":
      return "ci-badge ci-badge-strong";
    case "MODERATE":
      return "ci-badge ci-badge-moderate";
    case "WEAK":
      return "ci-badge ci-badge-weak";
    default:
      return "ci-badge ci-badge-na";
  }
}

/**
 * Cyber-Insurance Readiness Report (corporate account admins).
 * Aggregates human-layer controls across enrolled seats and combines them
 * with self-attested administrative controls into an underwriting-ready report.
 */
function CyberInsuranceReport() {
  const { loggedInUser } = useUser();
  const { t } = useTranslation();

  const [view, setView] = useState("list"); // "list" | "generate" | "detail"
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Generate-form state
  const [periodDays, setPeriodDays] = useState(90);
  const [attestations, setAttestations] = useState(() =>
    STANDARD_ATTESTATIONS.reduce((acc, a) => {
      acc[a.key] = { attested: false, notes: "" };
      return acc;
    }, {}),
  );

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await cyberInsuranceReportAPI.listReports(20);
      setReports(response.reports || []);
    } catch (err) {
      console.error("[CyberInsuranceReport] Error loading reports:", err);
      toast.error(t("ci.toast.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (loggedInUser?.uuid) {
      loadReports();
    }
  }, [loggedInUser, loadReports]);

  const toggleAttestation = (key) => {
    setAttestations((prev) => ({
      ...prev,
      [key]: { ...prev[key], attested: !prev[key].attested },
    }));
  };

  const updateAttestationNotes = (key, notes) => {
    setAttestations((prev) => ({
      ...prev,
      [key]: { ...prev[key], notes },
    }));
  };

  // Resolve a localized attestation control label from its stable key,
  // falling back to the English label persisted on the report.
  const attestationLabel = (att) => {
    if (att?.key) {
      const label = t(`ci.attest.${att.key}.label`);
      if (label !== `ci.attest.${att.key}.label`) return label;
    }
    return att?.control || "";
  };

  // Resolve a localized recommendation. New reports store structured
  // { code, params }; older reports stored a pre-formatted English string.
  const recommendationText = (rec) => {
    if (typeof rec === "string") return rec;
    if (rec?.code) return t(`ci.rec.${rec.code}`, rec.params);
    return rec?.text || "";
  };

  // Localized readiness-level label, falling back to the raw enum value.
  const levelLabel = (level) => {
    if (!level) return "";
    const key = `ci.level.${level}`;
    const value = t(key);
    return value !== key ? value : level;
  };

  // Resolve the localized headline summary. New reports store structured
  // { code, params }; older reports stored a pre-formatted English string.
  const summaryText = (r) => {
    const s = r?.readinessSummary;
    if (typeof s === "string") return s;
    if (!s?.code) return s?.text || "";
    const p = s.params || {};
    const clickRate = p.clickMeasured
      ? `${p.clickRate}%`
      : t("ci.summary.clickNotMeasured");
    return t(`ci.summary.${s.code}`, {
      ...p,
      level: levelLabel(p.level),
      clickRate,
    });
  };

  // Localized framework-mapping cell from its stable key, falling back to the
  // English value persisted on the report.
  const frameworkText = (fm, field) => {
    if (fm?.key) {
      const key = `ci.fw.${fm.key}.${field}`;
      const value = t(key);
      if (value !== key) return value;
    }
    return field === "name" ? fm?.controlName : fm?.kinshieldEvidence;
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const attestationArray = STANDARD_ATTESTATIONS.map((a) => ({
        key: a.key,
        attested: attestations[a.key].attested,
        notes: attestations[a.key].notes?.trim() || null,
      }));

      const report = await cyberInsuranceReportAPI.generateReport({
        periodDays,
        sendEmail: false,
        attestations: attestationArray,
      });

      toast.success(t("ci.toast.generated"));
      setSelectedReport(report);
      setView("detail");
      loadReports();
    } catch (err) {
      console.error("[CyberInsuranceReport] Error generating report:", err);
      const message =
        err?.response?.data?.message || t("ci.toast.generateFailed");
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleViewReport = async (reportId) => {
    try {
      setLoading(true);
      const report = await cyberInsuranceReportAPI.getReport(reportId);
      setSelectedReport(report);
      setView("detail");
    } catch (err) {
      console.error("[CyberInsuranceReport] Error opening report:", err);
      toast.error(t("ci.toast.openFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (reportId) => {
    try {
      setDownloadingPdf(true);
      const response = await cyberInsuranceReportAPI.getReportPdfUrl(reportId);
      if (response?.downloadUrl) {
        window.open(response.downloadUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error(t("ci.toast.pdfUnavailable"));
      }
    } catch (err) {
      console.error("[CyberInsuranceReport] Error downloading PDF:", err);
      toast.error(t("ci.toast.pdfFailed"));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDeleteReport = (reportId) => {
    showToastWithAction(t("ci.confirm.delete"), async () => {
      try {
        setDeletingId(reportId);
        await cyberInsuranceReportAPI.deleteReport(reportId);
        toast.success(t("ci.toast.deleted"));
        setReports((prev) => prev.filter((r) => r.reportId !== reportId));
      } catch (err) {
        console.error("[CyberInsuranceReport] Error deleting report:", err);
        toast.error(t("ci.toast.deleteFailed"));
      } finally {
        setDeletingId(null);
      }
    });
  };

  // ===================== RENDER: LIST =====================
  const renderList = () => (
    <div className="ci-section">
      <div className="ci-list-header">
        <div>
          <h3 className="ci-h3">{t("ci.list.title")}</h3>
          <p className="ci-muted">{t("ci.list.subtitle")}</p>
        </div>
        <button
          type="button"
          className="ci-btn ci-btn-primary"
          onClick={() => setView("generate")}
        >
          {t("ci.list.newReport")}
        </button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : reports.length === 0 ? (
        <div className="ci-empty">
          <p>{t("ci.list.emptyTitle")}</p>
          <p className="ci-muted">{t("ci.list.emptyBody")}</p>
          <button
            type="button"
            className="ci-btn ci-btn-primary"
            onClick={() => setView("generate")}
          >
            {t("ci.list.generate")}
          </button>
        </div>
      ) : (
        <div className="ci-table-wrap">
          <table className="ci-table ci-list-table">
            <thead>
              <tr>
                <th>{t("ci.list.colPeriod")}</th>
                <th>{t("ci.list.colReadiness")}</th>
                <th>{t("ci.list.colLevel")}</th>
                <th>{t("ci.list.colSeats")}</th>
                <th>{t("ci.list.colGenerated")}</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.reportId}>
                  <td data-label={t("ci.list.colPeriod")}>
                    {formatDate(r.periodStart)} – {formatDate(r.periodEnd)}
                  </td>
                  <td data-label={t("ci.list.colReadiness")}>
                    <span
                      className="ci-score-pill"
                      style={{
                        backgroundColor: cyberInsuranceReportAPI.getScoreColor(
                          r.readinessScore,
                        ),
                      }}
                    >
                      {r.readinessScore}/100
                    </span>
                  </td>
                  <td data-label={t("ci.list.colLevel")}>
                    {levelLabel(r.readinessLevel)}
                  </td>
                  <td data-label={t("ci.list.colSeats")}>{r.enrolledSeats}</td>
                  <td data-label={t("ci.list.colGenerated")}>
                    {formatDate(r.generatedAt)}
                  </td>
                  <td className="ci-actions">
                    <button
                      type="button"
                      className="ci-btn ci-btn-link"
                      onClick={() => handleViewReport(r.reportId)}
                    >
                      {t("ci.action.view")}
                    </button>
                    {r.hasPdf && (
                      <button
                        type="button"
                        className="ci-btn ci-btn-link"
                        onClick={() => handleDownloadPdf(r.reportId)}
                        disabled={downloadingPdf}
                      >
                        {t("ci.action.pdf")}
                      </button>
                    )}
                    <button
                      type="button"
                      className="ci-btn ci-btn-link ci-btn-danger"
                      onClick={() => handleDeleteReport(r.reportId)}
                      disabled={deletingId === r.reportId}
                    >
                      {deletingId === r.reportId
                        ? t("ci.action.deleting")
                        : t("ci.action.delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ===================== RENDER: GENERATE =====================
  const renderGenerate = () => (
    <div className="ci-section">
      <button
        type="button"
        className="ci-btn ci-btn-link ci-back"
        onClick={() => setView("list")}
      >
        {t("ci.back")}
      </button>

      <h3 className="ci-h3">{t("ci.generate.title")}</h3>
      <p className="ci-muted">{t("ci.generate.intro")}</p>

      <div className="ci-card">
        <label htmlFor="ci-period" className="ci-label">
          {t("ci.generate.period")}
        </label>
        <select
          id="ci-period"
          className="ci-input"
          value={periodDays}
          onChange={(e) => setPeriodDays(Number(e.target.value))}
        >
          {PERIOD_OPTIONS.map((value) => (
            <option key={value} value={value}>
              {t(`ci.period.${value}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="ci-card">
        <h4 className="ci-h4">{t("ci.generate.attestTitle")}</h4>
        <p className="ci-muted ci-mb">{t("ci.generate.attestIntro")}</p>
        <div className="ci-attest-list">
          {STANDARD_ATTESTATIONS.map((a) => (
            <div className="ci-attest-row" key={a.key}>
              <label className="ci-checkbox">
                <input
                  type="checkbox"
                  checked={attestations[a.key].attested}
                  onChange={() => toggleAttestation(a.key)}
                />
                <span>
                  <span className="ci-attest-label">
                    {t(`ci.attest.${a.key}.label`)}
                  </span>
                  <span className="ci-attest-help">
                    {t(`ci.attest.${a.key}.help`)}
                  </span>
                </span>
              </label>
              <input
                type="text"
                className="ci-input ci-notes"
                placeholder={t("ci.generate.notesPlaceholder")}
                value={attestations[a.key].notes}
                onChange={(e) => updateAttestationNotes(a.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="ci-generate-actions">
        <button
          type="button"
          className="ci-btn ci-btn-secondary"
          onClick={() => setView("list")}
          disabled={generating}
        >
          {t("ci.generate.cancel")}
        </button>
        <button
          type="button"
          className="ci-btn ci-btn-primary"
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? t("ci.generate.generating") : t("ci.generate.submit")}
        </button>
      </div>
    </div>
  );

  // ===================== RENDER: DETAIL =====================
  const renderControl = (title, control, rows) => {
    if (!control) return null;
    return (
      <div className="ci-control">
        <div className="ci-control-head">
          <span className="ci-control-title">{title}</span>
          <span className="ci-control-score">{control.componentScore}/100</span>
        </div>
        <span className={statusClass(control.status)}>
          {control.hasData === false ? t("ci.detail.noData") : control.status}
        </span>
        <table className="ci-metrics">
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td className="ci-metric-label">{row.label}</td>
                <td className="ci-metric-value">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDetail = () => {
    const r = selectedReport;
    if (!r) return null;
    const scoreColor = cyberInsuranceReportAPI.getScoreColor(r.readinessScore);

    return (
      <div className="ci-section">
        <button
          type="button"
          className="ci-btn ci-btn-link ci-back"
          onClick={() => {
            setSelectedReport(null);
            setView("list");
          }}
        >
          {t("ci.back")}
        </button>

        <div className="ci-detail-header">
          <div>
            <h3 className="ci-h3">
              {r.companyName || t("ci.detail.companyFallback")}
            </h3>
            <p className="ci-muted">
              {r.industry || "—"}
              {r.companySize ? ` · ${r.companySize}` : ""}
              {r.employeeCount
                ? ` · ${r.employeeCount} ${t("ci.detail.employees")}`
                : ""}
            </p>
            <p className="ci-muted">
              {formatDate(r.periodStart)} – {formatDate(r.periodEnd)} ·{" "}
              {r.enrolledSeats} {t("ci.detail.enrolledSeats")}
            </p>
          </div>
          <div className="ci-score-block" style={{ color: scoreColor }}>
            <div className="ci-score-value">{r.readinessScore}</div>
            <div className="ci-score-out">/ 100</div>
            <div className="ci-score-level">{levelLabel(r.readinessLevel)}</div>
          </div>
        </div>

        {r.readinessSummary && <p className="ci-summary">{summaryText(r)}</p>}

        <h4 className="ci-h4">{t("ci.detail.controlsTitle")}</h4>
        <div className="ci-controls-grid">
          {renderControl(t("ci.control.mfa"), r.mfa, [
            {
              label: t("ci.metric.mfaCoverage"),
              value: `${r.mfa?.coveragePercent ?? 0}%`,
            },
            {
              label: t("ci.metric.seatsWithMfa"),
              value: `${r.mfa?.enabledCount ?? 0} ${t("ci.metric.seatsOf")} ${
                r.mfa?.totalSeats ?? 0
              }`,
            },
          ])}
          {renderControl(t("ci.control.training"), r.training, [
            {
              label: t("ci.metric.membersTrained"),
              value: `${r.training?.membersTrained ?? 0} ${t(
                "ci.metric.seatsOf",
              )} ${r.training?.totalSeats ?? 0}`,
            },
            {
              label: t("ci.metric.completionRate"),
              value: `${r.training?.completionPercent ?? 0}%`,
            },
            {
              label: t("ci.metric.trainingsCompleted"),
              value: r.training?.totalTrainingsCompleted ?? 0,
            },
          ])}
          {renderControl(t("ci.control.phishing"), r.phishing, [
            {
              label: t("ci.metric.simulationsDelivered"),
              value: r.phishing?.simulationsDelivered ?? 0,
            },
            {
              label: t("ci.metric.clickRate"),
              value: `${r.phishing?.clickRate ?? 0}%`,
            },
            {
              label: t("ci.metric.reportRate"),
              value: `${r.phishing?.reportRate ?? 0}%`,
            },
          ])}
          {renderControl(t("ci.control.threats"), r.threats, [
            {
              label: t("ci.metric.analysesPerformed"),
              value: r.threats?.analysesPerformed ?? 0,
            },
            {
              label: t("ci.metric.scamsDetected"),
              value: r.threats?.scamsDetected ?? 0,
            },
          ])}
        </div>

        {Array.isArray(r.attestations) && r.attestations.length > 0 && (
          <>
            <h4 className="ci-h4">{t("ci.detail.attestTitle")}</h4>
            <div className="ci-table-wrap">
              <table className="ci-table">
                <thead>
                  <tr>
                    <th>{t("ci.detail.colControl")}</th>
                    <th>{t("ci.detail.colAttested")}</th>
                    <th>{t("ci.detail.colNotes")}</th>
                  </tr>
                </thead>
                <tbody>
                  {r.attestations.map((att) => (
                    <tr key={att.key || att.control}>
                      <td>{attestationLabel(att)}</td>
                      <td>
                        <span
                          className={att.attested ? "ci-att-yes" : "ci-att-no"}
                        >
                          {att.attested ? t("ci.yes") : t("ci.no")}
                        </span>
                      </td>
                      <td>{att.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {Array.isArray(r.recommendations) && r.recommendations.length > 0 && (
          <>
            <h4 className="ci-h4">{t("ci.detail.recommendations")}</h4>
            <ul className="ci-recs">
              {r.recommendations.map((rec, i) => (
                <li key={i}>{recommendationText(rec)}</li>
              ))}
            </ul>
          </>
        )}

        {Array.isArray(r.frameworkMappings) &&
          r.frameworkMappings.length > 0 && (
            <>
              <h4 className="ci-h4">{t("ci.detail.frameworkTitle")}</h4>
              <div className="ci-table-wrap">
                <table className="ci-table">
                  <thead>
                    <tr>
                      <th>{t("ci.detail.colFramework")}</th>
                      <th>{t("ci.detail.colControlId")}</th>
                      <th>{t("ci.detail.colName")}</th>
                      <th>{t("ci.detail.colEvidence")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.frameworkMappings.map((fm, i) => (
                      <tr key={i}>
                        <td>{fm.framework}</td>
                        <td>{fm.controlId}</td>
                        <td>{frameworkText(fm, "name")}</td>
                        <td>{frameworkText(fm, "evidence")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

        <div className="ci-generate-actions">
          <button
            type="button"
            className="ci-btn ci-btn-primary"
            onClick={() => handleDownloadPdf(r.reportId)}
            disabled={downloadingPdf}
          >
            {downloadingPdf
              ? t("ci.detail.preparing")
              : t("ci.detail.downloadPdf")}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="ci-container">
      <div className="ci-page-header">
        <h2 className="ci-h2">{t("ci.pageTitle")}</h2>
        <p className="ci-muted">{t("ci.pageSubtitle")}</p>
      </div>

      {view === "list" && renderList()}
      {view === "generate" && renderGenerate()}
      {view === "detail" && renderDetail()}
    </div>
  );
}

export default CyberInsuranceReport;
